<script setup lang="ts">
import type { NewSuggestInputProps } from '.';

import { CommandInput } from '@/components/SearchSuggestRadixCommand';
import { cn } from '@/utils';
import { useRafFn, useThrottleFn } from '@vueuse/core';

import { SEARCH_PLACEHOLDERS } from '.';

defineOptions({
	inheritAttrs: false
});

const emit = defineEmits<{
	(e: 'focus', payload: FocusEvent): void;
	(e: 'blur', payload: FocusEvent): void;
	(e: 'clear'): void;
	(e: 'close-modal'): void;
	(
		e: 'enter',
		event: KeyboardEvent,
		selectedElement: HTMLElement | undefined
	): void;
}>();

const props = withDefaults(
	defineProps<
		NewSuggestInputProps & {
			placeholder?: string;
		}
	>(),
	{
		placeholders: () => [...SEARCH_PLACEHOLDERS.animated],
		typingPeriod: 1500,
		typingSpeed: 75,
		enableAnimation: true,
		placeholder: '',
		showCloseIcon: false,
		focusedPlaceholder: ''
	}
);

const FOCUSED_PLACEHOLDER =
	'Endereço, bairro, cidade, condomínio e código do imóvel';
const modelValue = defineModel<string>('modelValue');

const animatedPlaceholder = shallowRef<string>('');
const placeholderIndex = shallowRef<number>(0);
const isDeleting = shallowRef<boolean>(true);
const isPaused = shallowRef<boolean>(false);
const lastUpdateTime = shallowRef<number>(0);
const isFirstRender = shallowRef<boolean>(true);
const isFocused = shallowRef<boolean>(false);

const placeholderCount = computed<number>(() =>
	Array.isArray(props.placeholders) ? props.placeholders?.length || 0 : 0
);

const currentPlaceholder = computed<string>(() => {
	const index = placeholderIndex.value % placeholderCount.value;
	return props.placeholders[index] || '';
});

const computedPlaceholder = computed<string>(() => {
	// Quando o input está focado, usar o placeholder focado
	if (isFocused.value) {
		return FOCUSED_PLACEHOLDER;
	}

	// Usar o dynamicPlaceholder que é passado via props.placeholder pelo componente pai
	if (props.placeholder) {
		return props.placeholder;
	}

	// Fallback para o placeholder focado
	return FOCUSED_PLACEHOLDER;
});

const { pause, resume } = useRafFn(
	() => {
		if (!placeholderCount.value || isPaused.value) return;

		const now = performance.now();
		const elapsed = now - lastUpdateTime.value;

		const delay = calculateDelay();

		if (elapsed >= delay) {
			updatePlaceholderText();
			lastUpdateTime.value = now;
		}
	},
	{ immediate: false }
);

const updatePlaceholderText = useThrottleFn(async () => {
	if (isDeleting.value) {
		animatedPlaceholder.value = currentPlaceholder.value.substring(
			0,
			animatedPlaceholder.value.length - 1
		);

		if (animatedPlaceholder.value === '') {
			isPaused.value = true;
			const timeout = setTimeout(() => {
				isPaused.value = false;
				isDeleting.value = false;
				placeholderIndex.value =
					(placeholderIndex.value + 1) % placeholderCount.value;
				clearTimeout(timeout);
			}, 500);
		}
	} else {
		if (animatedPlaceholder.value === '') {
			animatedPlaceholder.value = currentPlaceholder.value.charAt(0);
		} else if (
			animatedPlaceholder.value.length < currentPlaceholder.value.length
		) {
			animatedPlaceholder.value = currentPlaceholder.value.substring(
				0,
				animatedPlaceholder.value.length + 1
			);

			if (animatedPlaceholder.value === currentPlaceholder.value) {
				isPaused.value = true;
				setTimeout(() => {
					isPaused.value = false;
					isDeleting.value = true;
				}, props.typingPeriod);
			}
		}
	}

	isFirstRender.value = false;
}, 16);

function calculateDelay(): number {
	let delay = props.typingSpeed || 150;

	if (isDeleting.value) {
		delay /= 2;
	}

	return delay;
}

/**
 * Starts the animation cycle
 */
function startAnimation() {
	animatedPlaceholder.value = isFirstRender.value
		? currentPlaceholder.value
		: '';
	isDeleting.value = isFirstRender.value ? true : false;
	placeholderIndex.value = 0;
	isPaused.value = false;
	lastUpdateTime.value = performance.now();

	if (isFirstRender.value && isDeleting.value) {
		setTimeout(() => {
			resume();
		}, 2500);
	} else {
		resume();
	}
}

/**
 * Sets the placeholder text to the focused placeholder
 */
function handleFocus(event: FocusEvent) {
	pause();
	animatedPlaceholder.value = FOCUSED_PLACEHOLDER;
	isFocused.value = true;

	emit('focus', event);
}

function handleBlur(event: FocusEvent) {
	validateAndStartAnimation();
	emit('blur', event);
}

function handleClear() {
	if (props.enableAnimation) {
		validateAndStartAnimation();
	}
	emit('clear');
}

/**
 * Validates and starts the animation cycle
 */
function validateAndStartAnimation() {
	isFocused.value = false;

	if (props.enableAnimation && placeholderCount.value) {
		startAnimation();
	}
}

const commandInputEl =
	useTemplateRef<InstanceType<typeof CommandInput>>('commandInputEl');
const visibleInputEl =
	useTemplateRef<InstanceType<typeof CommandInput>>('visibleInputEl');

/**
 * Focus the input element
 */
function focus() {
	// Foca no input visível em vez do escondido
	const inputElement = visibleInputEl.value?.$el?.querySelector('input');
	if (inputElement instanceof HTMLInputElement) {
		inputElement.focus();
	}
}

/**
 * Blur the input element
 */
function blur() {
	const inputElement = commandInputEl.value?.$el?.querySelector('input');
	if (inputElement) {
		inputElement.blur();
	}
}

defineExpose({
	focus,
	blur
});

watch(
	() => modelValue.value,
	() => {
		if (props.enableAnimation) {
			nextTick(() => {
				validateAndStartAnimation();
			});
		}
	}
);

watch(
	() => props.placeholders,
	() => {
		validateAndStartAnimation();
	}
);

onMounted(() => {
	if (props.enableAnimation) {
		validateAndStartAnimation();
	}
});

onBeforeUnmount(() => {
	pause();
});

function handleEnter(
	event: KeyboardEvent,
	selectedElement: HTMLElement | undefined
) {
	event.preventDefault();
	event.stopPropagation();

	emit('enter', event, selectedElement);
}
</script>

<template>
	<div class="relative">
		<!-- Input escondido que mantém o valor real para o Command -->
		<CommandInput
			ref="commandInputEl"
			v-model="modelValue"
			class="sr-only"
			:placeholder="computedPlaceholder"
			@focus="handleFocus"
			@blur="handleBlur"
			@enter.stop.prevent="handleEnter"
		/>

		<!-- Input visível que sempre exibe o placeholder dinâmico -->
		<CommandInput
			ref="visibleInputEl"
			model-value=""
			:placeholder="computedPlaceholder"
			:class="cn('focus:placeholder:opacity-60', props.class)"
			:wrapper-classes="wrapperClasses"
			@focus="handleFocus"
			@blur="handleBlur"
			@enter.stop.prevent="handleEnter"
			@input="
				(event: Event) => {
					const target = event.target as HTMLInputElement;
					modelValue = target.value;
				}
			"
		>
			<template #prefix>
				<slot name="prefix" />
			</template>
			<template #suffix>
				<slot name="suffix">
					<div
						:class="
							cn(
								'flex h-full w-7 items-center justify-end',
								inputClearIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightCancel
							v-show="modelValue"
							:class="
								cn(
									'size-5 cursor-pointer text-white-dark-3',
									inputClearIconClasses
								)
							"
							@mousedown.prevent.stop="handleClear"
						/>
					</div>
					<button
						v-if="showCloseIcon"
						class="mr-4 flex size-8 items-center justify-center"
						@mousedown.prevent
						@click.stop="emit('close-modal')"
					>
						<IconMaterialSymbolsLightClose class="size-8 text-primary" />
					</button>
					<div
						:class="
							cn(
								'grid h-full w-[62px] place-items-center',
								inputSearchIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightSearch
							:class="cn('size-7 text-primary', inputSearchIconClasses)"
						/>
					</div>
				</slot>
			</template>
		</CommandInput>
	</div>
</template>
